// Infinity (a manufacturing line or solution with no score -- not answered yet, unmanufacturable, or given up on) can
// be on both sides, and Infinity - Infinity is NaN, which no sort can order.
export const compareScores = (a, b) => {
  if (a === b) { return 0; }
  return a < b ? -1 : 1;
};

// THE `maximum` CHEAPEST WAYS TO PICK ONE ITEM FROM EACH PART, where a combination costs the SUM of its items'
// scores -- cheapest first, each combination holding its items in PART order. Shared by the manufacturing-line
// listing (a manufacturing line list's score is the sum of its manufacturing lines' best scores) and the solutions
// grid (a solution group's score is the sum of its solutions').
//
// The full product is never built. Each part is sorted cheapest first, and the search starts from the combination
// of every part's cheapest item: the next cheapest combination is always one step (one part moved to its next
// item) from a combination already taken, so only the neighbours of what has been taken are ever looked at --
// about maximum x parts combinations, however large the product is.
//
// Ties keep a stable order: equal scores keep the order the part arrived in, and equal totals keep the order of
// the picks within each part.
// `accepts`, when given, leaves out a combination it rejects: the search still goes on through it to the ones after
// it, so the result is still the `maximum` cheapest of the combinations it accepts.
export const cheapestCombinations = (perPart, scoreOf, maximum, accepts = () => true) => {
  // The maximum comes from a page global (SystemParam); a missing or broken one must not quietly empty the list.
  if (!Number.isInteger(maximum) || maximum <= 0) {
    throw new Error(`cheapestCombinations needs a positive whole maximum, got ${maximum}`);
  }
  if (perPart.length === 0 || perPart.some((items) => items.length === 0)) { return []; }

  const sortedParts = perPart.map((items) => items
    .map((item, position) => ({ item, position, score: scoreOf(item) }))
    .sort((a, b) => compareScores(a.score, b.score) || a.position - b.position));

  const totalOf = (picks) => picks.reduce((sum, pick, part) => sum + sortedParts[part][pick].score, 0);
  const compareCandidates = (a, b) => {
    const byTotal = compareScores(a.total, b.total);
    if (byTotal !== 0) { return byTotal; }
    const differingPart = a.picks.findIndex((pick, part) => pick !== b.picks[part]);
    return differingPart === -1 ? 0 : a.picks[differingPart] - b.picks[differingPart];
  };

  // A binary min-heap of candidates, cheapest on top.
  const heap = [];
  const push = (candidate) => {
    heap.push(candidate);
    let child = heap.length - 1;
    while (child > 0) {
      const parent = Math.floor((child - 1) / 2);
      if (compareCandidates(heap[child], heap[parent]) >= 0) { break; }
      [heap[child], heap[parent]] = [heap[parent], heap[child]];
      child = parent;
    }
  };
  const pop = () => {
    const top = heap[0];
    const last = heap.pop();
    if (heap.length > 0) {
      heap[0] = last;
      let parent = 0;
      for (;;) {
        const left = parent * 2 + 1;
        const right = left + 1;
        let smallest = parent;
        if (left < heap.length && compareCandidates(heap[left], heap[smallest]) < 0) { smallest = left; }
        if (right < heap.length && compareCandidates(heap[right], heap[smallest]) < 0) { smallest = right; }
        if (smallest === parent) { break; }
        [heap[parent], heap[smallest]] = [heap[smallest], heap[parent]];
        parent = smallest;
      }
    }
    return top;
  };

  const queued = new Set();
  const enqueue = (picks) => {
    const key = picks.join(',');
    if (queued.has(key)) { return; }
    queued.add(key);
    push({ picks, total: totalOf(picks) });
  };

  const combinations = [];
  enqueue(sortedParts.map(() => 0));
  while (combinations.length < maximum && heap.length > 0) {
    const { picks } = pop();
    const combination = picks.map((pick, part) => sortedParts[part][pick].item);
    if (accepts(combination)) { combinations.push(combination); }
    picks.forEach((pick, part) => {
      if (pick + 1 >= sortedParts[part].length) { return; }
      const next = [...picks];
      next[part] = pick + 1;
      enqueue(next);
    });
  }
  return combinations;
};
